import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, MapPin, MessageCircle, Star, HardHat, ShieldCheck, Send, Users, Loader2, SearchX } from "lucide-react";
import { api } from "@/lib/api";
import { useServices, useServiceMap, useCities, loadLocation, saveLocation } from "@/lib/data";
import { ServiceIcon } from "@/lib/icons";
import { ServiceSearchBox, SelectedService } from "@/components/ServiceSearch";
import LocationAsk from "@/components/LocationAsk";
import ProviderMap from "@/components/ProviderMap";
import ProviderCard from "@/components/ProviderCard";
import { InstallHelp } from "@/components/InstallHelp";
import { useAuth } from "@/context/AuthContext";

const STEPS = [
  { icon: Search, title: "Escolha o serviço", text: "Digite o que você precisa no campo de busca." },
  { icon: MapPin, title: "Achamos alguém perto", text: "Oferecemos seu pedido ao prestador mais próximo, um de cada vez." },
  { icon: MessageCircle, title: "Combine pelo WhatsApp", text: "Quando ele aceitar, vocês conversam e combinam o orçamento." },
  { icon: Star, title: "Avalie", text: "Depois do serviço, dê sua nota de 1 a 5 estrelas." },
];

function PopularChips({ onPick }) {
  const { data = [] } = useQuery({ queryKey: ["popular"], queryFn: () => api.get("/services/popular").then((r) => r.data) });
  return (
    <div className="mt-3 flex flex-wrap gap-2" data-testid="popular-chips">
      {data.map((s) => (
        <button key={s.slug} onClick={() => onPick(s)} data-testid={`popular-chip-${s.slug}`}
          className="inline-flex min-h-[48px] items-center gap-2 rounded-full border-2 border-orange-200 bg-white px-4 text-base font-bold text-slate-800 transition-colors hover:border-orange-500 hover:bg-orange-50">
          <ServiceIcon icon={s.icon} className="h-5 w-5 text-orange-500" /> {s.name}
        </button>
      ))}
    </div>
  );
}

function HomeInfo() {
  const { data: cities = [] } = useCities();
  const { user } = useAuth();
  return (
    <>
      <section className="mx-auto mt-14 max-w-5xl px-4 sm:px-6">
        <h2 className="font-display text-3xl font-bold text-slate-900">Como funciona</h2>
        <ol className="mt-6 grid gap-4 sm:grid-cols-2">
          {STEPS.map((s, i) => (
            <li key={s.title} className="flex gap-4 rounded-3xl bg-slate-50 p-5" data-testid={`how-step-${i + 1}`}>
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white font-display text-2xl font-extrabold text-orange-500 shadow-sm">{i + 1}</span>
              <div>
                <p className="flex items-center gap-2 text-xl font-bold text-slate-900"><s.icon className="h-6 w-6 text-orange-500" /> {s.title}</p>
                <p className="mt-1 text-lg text-slate-700">{s.text}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>
      <section className="mx-auto mt-14 max-w-5xl px-4 sm:px-6">
        <div className="grid gap-6 rounded-3xl bg-orange-500 p-6 text-white sm:grid-cols-[1.4fr_1fr] sm:p-10">
          <div>
            <HardHat className="h-12 w-12" />
            <h2 className="mt-3 font-display text-3xl font-extrabold">Você é prestador de serviço?</h2>
            <p className="mt-2 text-lg text-orange-50">Receba pedidos de clientes perto de você. Os primeiros 5 pedidos aceitos são grátis.</p>
          </div>
          <div className="flex flex-col justify-center gap-3">
            <Link to={user ? "/perfil/prestador" : "/cadastro?next=/perfil/prestador"} className="po-btn min-h-[68px] bg-white text-xl text-orange-600 shadow-lg hover:bg-orange-50" data-testid="become-provider-button">
              Sou prestador, quero participar
            </Link>
            <p className="flex items-center gap-2 text-base text-orange-50"><ShieldCheck className="h-5 w-5" /> Todo cadastro é analisado antes de aparecer.</p>
          </div>
        </div>
      </section>
      <section className="mx-auto mt-14 max-w-5xl px-4 sm:px-6">
        <h2 className="font-display text-2xl font-bold text-slate-900">Onde atendemos</h2>
        <div className="mt-4 flex flex-wrap gap-2" data-testid="cities-list">
          {cities.map((c) => <span key={c.id} className="rounded-full border-2 border-slate-200 px-4 py-2 text-base font-semibold text-slate-700">{c.name}</span>)}
        </div>
        <div className="mt-8 max-w-xl"><InstallHelp /></div>
      </section>
    </>
  );
}

export default function Home() {
  const [sp, setSp] = useSearchParams();
  const slug = sp.get("servico") || "";
  const svc = useServiceMap()[slug];
  useServices();
  const [loc, setLoc] = useState(loadLocation());
  const [askLoc, setAskLoc] = useState(!loadLocation());
  const params = loc?.mode === "gps" ? { lat: loc.lat, lng: loc.lng } : { city: loc?.city || "Goiânia", neighborhood: loc?.neighborhood || "Centro" };
  const { data, isLoading } = useQuery({
    queryKey: ["providers", slug, params],
    queryFn: () => api.get("/providers", { params: { ...(slug ? { service: slug } : {}), ...params } }).then((r) => r.data),
    refetchInterval: 30000,
  });
  const pick = (s) => setSp(s ? { servico: s.slug } : {});
  const chooseLoc = (l) => { saveLocation(l); setLoc(l); setAskLoc(false); };
  const providers = data?.providers || [];
  const where = !loc ? "Goiânia (aproximado)" : loc.mode === "gps" ? (loc.neighborhood ? `${loc.neighborhood}, ${loc.city}` : loc.city || "Sua localização") : `${loc.neighborhood}, ${loc.city}`;

  return (
    <div>
      <section className="mx-auto max-w-5xl space-y-4 px-4 pt-6 sm:px-6">
        <h1 className="font-display text-3xl font-extrabold text-slate-900 sm:text-4xl">O que você precisa?</h1>
        {svc ? <SelectedService service={svc} onClear={() => pick(null)} testId="home-selected-service" /> : (
          <>
            <ServiceSearchBox onPick={pick} testId="home-service-search" />
            <PopularChips onPick={pick} />
          </>
        )}
        {askLoc ? <LocationAsk onLoc={chooseLoc} /> : (
          <button onClick={() => setAskLoc(true)} className="text-lg font-semibold text-orange-600 underline" data-testid="change-location-button">
            <MapPin className="inline h-5 w-5" /> {where} · trocar local
          </button>
        )}
        {data ? <ProviderMap origin={data.origin} providers={providers} serviceSlug={slug} /> : <div className="flex h-[220px] items-center justify-center rounded-3xl bg-slate-100 sm:h-[380px]"><Loader2 className="h-10 w-10 animate-spin text-orange-500" /></div>}
        {svc ? (
          <Link to={`/pedido/novo?servico=${slug}`} className="po-btn-primary min-h-[72px] w-full text-xl" data-testid="open-request-button">
            <Send className="h-7 w-7" /> Pedir para o mais próximo disponível
          </Link>
        ) : (
          <button disabled className="po-btn-primary min-h-[72px] w-full text-xl" data-testid="open-request-button">
            <Send className="h-7 w-7" /> Escolha um serviço para pedir
          </button>
        )}
        <h2 className="flex items-center gap-2 font-display text-2xl font-bold text-slate-900" data-testid="providers-count">
          <Users className="h-7 w-7 text-orange-500" /> {isLoading ? "..." : providers.length} {svc ? `de ${svc.name} ` : ""}disponíveis agora
        </h2>
        {svc && !isLoading && providers.length === 0 && (
          <div className="rounded-3xl border-2 border-orange-400 bg-orange-50 p-6 sm:p-8" data-testid="providers-empty">
            <div className="flex items-start gap-4">
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-orange-500 text-white"><SearchX className="h-8 w-8" /></span>
              <div>
                <p className="font-display text-2xl font-extrabold text-slate-900 sm:text-3xl" data-testid="providers-empty-title">Nenhum prestador disponível agora</p>
                <p className="mt-2 text-lg text-slate-700">Não achamos prestador de {svc.name} disponível perto de você neste momento. Envie o pedido assim mesmo: avisamos você quando alguém aceitar.</p>
              </div>
            </div>
            <Link to={`/pedido/novo?servico=${slug}`} className="po-btn-primary mt-6 min-h-[72px] w-full text-xl" data-testid="providers-empty-request-button">
              <Send className="h-7 w-7" /> Enviar pedido mesmo assim
            </Link>
            <button onClick={() => { pick(null); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="po-btn-outline mt-3 min-h-[60px] w-full text-lg" data-testid="providers-empty-other-service">
              <Search className="h-6 w-6" /> Tentar outro serviço
            </button>
          </div>
        )}
        {svc && <div className="grid gap-4 md:grid-cols-2">{providers.map((p) => <ProviderCard key={p.id} p={p} serviceSlug={slug} />)}</div>}
      </section>
      <HomeInfo />
    </div>
  );
}
